<template>
  <div
    class="d-flex flex-column white align-center justify-center"
    style="height: 100vh;"
  >
    <div class="mb-8">
      <img :src="logo" height="35px" />
    </div>

    <div class="d-flex align-center justify-center flex-column px-14 mb-3">
      <p
        class="blue-grey--text text--darken-3 font-weight-bold mb-10 text-center"
      >
        {{ title }}
      </p>

      <p class="body-1 blue-grey--text text--darken-3 mb-8 text-center">
        {{ subtitle }}
      </p>

      <div>
        <div
          v-for="(option, index) in details"
          class="mb-5 d-flex align-center"
          :key="`detail-${index}`"
        >
          <v-icon class="mr-4" color="blue-grey darken-2" size="24">
            {{ option.icon }}
          </v-icon>
          <p class="blue-grey--text text--darken-3 ma-0 pa-0 caption">
            {{ option.text }}
          </p>
        </div>
      </div>
    </div>

    <p class="mb-10 body-1 blue-grey--text text--darken-3">
      {{ this.$t('excel.welcome.subtitle_final') }}
    </p>

    <Button
      color="primary"
      type="submit"
      :text="$t('common.login')"
      @click="onSubmit"
    />
    <Alert
      :show="showErrorAlert"
      :text="$t('common.default_error')"
      @close="showErrorAlert = false"
    />
  </div>
</template>

<script>
import store from '@/store/index.js'
import config from '@/config.js'
import api from '@/api'

import Logo from '@/assets/logo.svg'
import Button from '@/ui/shared/components/Button.vue'
import Alert from '@/ui/shared/components/Alert.vue'

const HOSTS = {
  powerpoint: 'PowerPoint',
  excel: 'Excel',
}

export default {
  name: 'WelcomeView',

  data() {
    return {
      dialog: null,
      logo: Logo,
      host: window.Office.context.host,
      HOSTS,
      showErrorAlert: false,
    }
  },

  components: {
    Button,
    Alert,
  },

  created() {
    if (this.host === HOSTS.powerpoint) {
      store.setApplication('powerpoint')
    } else {
      store.setApplication('excel')
    }

    this.checkSession()
  },

  computed: {
    title() {
      if (this.host === HOSTS.powerpoint) {
        return this.$t('powerpoint.welcome.title')
      }

      if (this.host === HOSTS.excel) {
        return this.$t('excel.welcome.title')
      }

      return ''
    },

    subtitle() {
      if (this.host === HOSTS.powerpoint) {
        return this.$t('powerpoint.welcome.subtitle')
      }

      if (this.host === HOSTS.excel) {
        return this.$t('excel.welcome.subtitle')
      }

      return ''
    },

    details() {
      if (this.host === HOSTS.powerpoint) {
        return [
          {
            icon: 'mdi-filter-variant',
            text: this.$t('powerpoint.welcome.content.first'),
          },
          {
            icon: 'mdi-format-textbox',
            text: this.$t('powerpoint.welcome.content.second'),
          },
          {
            icon: 'mdi-pencil-box-outline',
            text: this.$t('powerpoint.welcome.content.third'),
          },
        ]
      }

      if (this.host === HOSTS.excel) {
        return [
          {
            icon: 'mdi-sticker-check-outline',
            text: this.$t('excel.welcome.content.first'),
          },
          {
            icon: 'mdi-list-status',
            text: this.$t('excel.welcome.content.second'),
          },
          {
            icon: 'mdi-filter-variant',
            text: this.$t('excel.welcome.content.third'),
          },
        ]
      }

      return []
    },
  },

  methods: {
    onSubmit() {
      console.log(`Opening Dialog to ${config.self}`)

      window.Office.context.ui.displayDialogAsync(
        `${config.self}/#/redirect`,
        {
          width: 50,
          height: 80,
        },
        result => {
          if (result.status === 'failed') {
            // In addition to general system errors, there are 3 specific errors for
            // displayDialogAsync that you can handle individually.
            switch (result.error.code) {
              case 12004:
                this.showNotification('Domain is not trusted')
                break
              case 12005:
                this.showNotification('HTTPS is required')
                break
              case 12007:
                this.showNotification('A dialog is already opened.')
                break
              default:
                this.showNotification(result.error.message)
                break
            }
          } else {
            this.dialog = result.value

            this.dialog.addEventHandler(
              window.Office.EventType.DialogMessageReceived,
              this.handleLogin
            )
          }
        }
      )
    },

    showNotification(message) {
      console.log(message)
    },

    async handleLogin(arg) {
      try {
        const data = JSON.parse(arg.message)
        if (data.accessToken && data.account._id) {
          store.access.setSession(data)
          this.dialog.close()

          this.$track('msOffice.login', {
            officeEnv: window.Office.context.host,
            status: 'success',
          })

          const [
            orgStructure,
            countryCodes,
            organizationCategories,
          ] = await Promise.all([
            api.organization.getOrganizationStructure(
              data.account.organization._id
            ),
            api.country.getAllCountryCodes(data.account.organization._id),
            api.organization.getOrganizationCategories(
              data.account.organization._id
            ),
          ])

          store.organization.setOrganizationStructure(orgStructure?.data)
          store.organization.setCountryCodes(countryCodes?.data)
          store.organization.setOrganizationCategories(
            organizationCategories?.data
          )

          if (window.Office.context.host === HOSTS.powerpoint) {
            return this.$router.push({
              name: 'powerpoint',
            })
          }

          return this.$router.push({
            name: 'excel',
          })
        }
        throw new Error(`No data access token or account, ${data}`)
      } catch (err) {
        this.$track('msOffice.login', {
          officeEnv: window.Office.context.host,
          status: 'fail',
        })
        console.log('Error', err)
      }
    },

    checkSession() {
      try {
        if (store.access.isLoggedIn()) {
          return this.$router.replace({
            name: this.host === HOSTS.powerpoint ? 'powerpoint' : 'excel',
          })
        }
      } catch (err) {
        this.showErrorAlert = true
      }
    },
  },
}
</script>
